<script setup lang="ts">
/**
 * Beneficial Owner Notices 문서 내 [[변수]] 자리.
 *   · date → 인라인 + 📅
 *   · person/entity → 인라인 + ✨ (챗봇 lookup)
 *   · address → 인라인 + 📍
 *   · text → 인라인 직접 편집
 */
import { computed, inject, ref, onMounted, watch, nextTick } from 'vue';
import { BO_NOTICE_DOC_CTX } from './boNoticeContext';

const props = defineProps<{ fieldKey: string; placeholder?: string }>();
const ctx = inject(BO_NOTICE_DOC_CTX)!;

const value = computed(() => ctx.valueOf(props.fieldKey));
const label = computed(() => props.placeholder || ctx.labelOf(props.fieldKey));
const fieldType = computed(() => ctx.typeOf(props.fieldKey));
const isDate = computed(() => fieldType.value === 'date');
const isAddress = computed(() => fieldType.value === 'address');
const showIcon = computed(() => ['person', 'entity', 'date', 'address'].includes(fieldType.value));
const active = computed(() => ctx.activeKey.value === props.fieldKey);

function onIcon() {
  if (isAddress.value) ctx.openAddress(props.fieldKey);
  else ctx.activate(props.fieldKey);
}
const iconName = computed(() => (
  isDate.value ? 'mdi-calendar-month'
    : isAddress.value ? 'mdi-map-marker-outline'
      : 'mdi-creation'
));
const iconClass = computed(() => (
  isDate.value ? 'ic-date'
    : isAddress.value ? 'ic-addr'
      : 'ic-ai'
));
const iconTip = computed(() => (
  isDate.value ? 'Pick a date'
    : isAddress.value ? 'Enter address'
      : 'Look up from chat'
));

const ed = ref<HTMLElement>();
onMounted(() => { if (ed.value) ed.value.textContent = value.value; });
watch(value, async (v) => {
  await nextTick();
  if (ed.value && document.activeElement !== ed.value && ed.value.textContent !== v) ed.value.textContent = v;
});
function onInput() {
  if (!ed.value) return;
  const txt = ed.value.textContent || '';
  if (!txt && ed.value.innerHTML) ed.value.innerHTML = '';
  ctx.setFieldValue(props.fieldKey, txt);
}
function clearField() {
  ctx.setFieldValue(props.fieldKey, '');
  if (ed.value) ed.value.innerHTML = '';
}

const dragOver = ref(false);
function onDrop(e: DragEvent) {
  dragOver.value = false;
  let v = '';
  const json = e.dataTransfer?.getData('application/x-entity-field');
  if (json) { try { v = String(JSON.parse(json).value ?? ''); } catch { /* noop */ } }
  if (!v) v = e.dataTransfer?.getData('text/plain') || '';
  if (v) ctx.setFieldValue(props.fieldKey, v.trim());
}

const placeable = computed(() => !!ctx.pendingPickValue.value);
function onFieldMousedown(e: MouseEvent) {
  if (!ctx.pendingPickValue.value) return;
  e.preventDefault();
  ctx.placePick(props.fieldKey);
}
</script>

<template>
  <span
    class="bonf" :class="{ empty: !value, filled: !!value, active, dragover: dragOver, placeable }"
    @mousedown.capture="onFieldMousedown"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  >
    <span
      ref="ed" class="bonf-in" :data-ph="label" contenteditable="true" spellcheck="false"
      @input="onInput" @keydown.enter.prevent="ed?.blur()"
    />
    <button
      v-if="showIcon"
      class="bonf-ic" :class="iconClass" :title="iconTip"
      @mousedown.prevent @click.stop="onIcon"
    >
      <v-icon size="12">{{ iconName }}</v-icon>
    </button>
    <button
      v-if="!!value"
      class="bonf-clear" title="Clear"
      @mousedown.prevent @click.stop="clearField"
    >
      <v-icon size="11">mdi-close</v-icon>
    </button>
  </span>
</template>

<style scoped>
.bonf { display:inline-flex; align-items:center; gap:3px; border-radius:4px; padding:0 3px; vertical-align:baseline; }
.bonf.empty { background:#fff6d6; box-shadow:inset 0 -2px 0 #f2c94c; }
.bonf.filled { background:#eaf3ff; box-shadow:inset 0 -2px 0 #1B84FF; }
.bonf.active { outline:2px solid #1B84FF; outline-offset:1px; }
.bonf.dragover { outline:2px dashed #22a06b; background:#e6f7ee; }
.bonf.placeable { outline:1.5px dashed #7bb0ff; cursor:copy; }
.bonf.placeable:hover { background:#eaf3ff; }
.bonf-in { min-width:46px; outline:none; font:inherit; color:#10305c; white-space:nowrap; }
.bonf.filled .bonf-in { font-weight:600; }
.bonf-in:empty::before { content:attr(data-ph); color:#9a7b1a; font-style:italic; font-size:.9em; cursor:text; }
.bonf-ic { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; border:none; cursor:pointer; flex:0 0 auto; padding:0; }
.bonf-ic.ic-ai { background:#eaf3ff; color:#1B84FF; }
.bonf-ic.ic-date { background:#eafaf1; color:#22a06b; }
.bonf-ic.ic-addr { background:#fdeefe; color:#a23bb0; }
.bonf-ic:hover { filter:brightness(.94); }
.bonf-clear { display:none; align-items:center; justify-content:center; width:15px; height:15px; border-radius:50%; border:none; background:#c4ccd6; color:#fff; cursor:pointer; flex:0 0 auto; padding:0; }
.bonf:hover .bonf-clear { display:inline-flex; }
.bonf-clear:hover { background:#8b95a1; }
</style>
